import React, { useState, useEffect } from 'react';
import Card from 'components/card';
import Switch from 'components/switch';
import AxiosInstance from 'axiosConfig';

const ChannelConfig = ({ channel_name, channel_par, channel_ext_ref, initialConfig, onSave }) => {
  const [config, setConfig] = useState({
    is_translation_on: false,
    gen_ai_enabled: false,
    enforces_t_n_c: false,
    entry_status: false,
    human_agents_on: false,
    anti_spam_on: false,
    extend_bot: false,
    min_ai_words: 0,
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Validate props
  useEffect(() => {
    if (
      (channel_name && typeof channel_name !== 'string') ||
      (channel_par && typeof channel_par !== 'string') ||
      (channel_ext_ref && typeof channel_ext_ref !== 'string')
    ) {
      console.error('Invalid prop types:', {
        channel_name: typeof channel_name,
        channel_par: typeof channel_par,
        channel_ext_ref: typeof channel_ext_ref,
      });
      setError('Invalid channel configuration data');
      setSuccess(null);
    }
  }, [channel_name, channel_par, channel_ext_ref]);

  // Fetch existing configuration using POST with channel_name, channel_par, channel_ext_ref
  useEffect(() => {
    const fetchConfig = async () => {
      if (channel_name && channel_par && channel_ext_ref) {
        try {
          const payload = {
            channel_name: String(channel_name),
            channel_par: String(channel_par),
            channel_ext_ref: String(channel_ext_ref),
          };
          console.log('Fetching config with payload:', payload);
          const response = await AxiosInstance.post('/channel-config', payload);
          if (response.data) {
            setConfig((prev) => ({ ...prev, ...response.data }));
            setError(null);
            setSuccess(null);
          }
        } catch (err) {
          console.error('Fetch config error:', err);
          setError(err.response?.data?.message || 'Failed to fetch existing configuration');
          setSuccess(null);
        }
      } else {
        setError('Missing required channel configuration data');
        setSuccess(null);
      }
    };

    if (initialConfig) {
      setConfig((prev) => ({ ...prev, ...initialConfig }));
      setError(null);
      setSuccess(null);
    } else {
      fetchConfig();
    }
  }, [channel_name, channel_par, channel_ext_ref, initialConfig]);

  const handleSwitchChange = (key) => (event) => {
    const checked = event.target.checked; // Extract boolean from event
    console.log(`Updating ${key}:`, checked); // Debug log
    setConfig((prev) => ({ ...prev, [key]: checked }));
    setError(null);
    setSuccess(null);
  };

  const handleMinWordsChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    console.log('Updating min_ai_words:', value); // Debug log
    setConfig((prev) => ({ ...prev, min_ai_words: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async () => {
    const payload = {
      channel_name: String(channel_name || ''),
      channel_par: String(channel_par || ''),
      channel_ext_ref: String(channel_ext_ref || ''),
      ...config,
    };

    // Validate config fields
    const booleanFields = [
      'is_translation_on',
      'gen_ai_enabled',
      'enforces_t_n_c',
      'entry_status',
      'human_agents_on',
      'anti_spam_on',
      'extend_bot',
    ];
    for (const field of booleanFields) {
      if (typeof config[field] !== 'boolean') {
        console.error(`Invalid ${field}:`, config[field]);
        setError(`Invalid configuration: ${field} must be a boolean`);
        setSuccess(null);
        return;
      }
    }
    if (typeof config.min_ai_words !== 'number') {
      console.error('Invalid min_ai_words:', config.min_ai_words);
      setError('Invalid configuration: min_ai_words must be a number');
      setSuccess(null);
      return;
    }

    // Log payload for debugging
    console.log('Submitting payload:', payload);

    // Test serialization
    try {
      JSON.stringify(payload);
    } catch (err) {
      console.error('Payload serialization error:', err);
      setError('Invalid configuration data: contains non-serializable values');
      setSuccess(null);
      return;
    }

    try {
      const response = await AxiosInstance.post('/channel-config-edit', payload);
      console.log('Configuration saved successfully', response.data);
      setSuccess(response.data.message || 'Configuration saved successfully');
      if (onSave) {
        onSave(payload);
      }
      setError(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save configuration');
      setSuccess(null);
    }
  };

  const switchConfigs = [
    { id: 'switch1', key: 'is_translation_on', label: config.is_translation_on?'Auto-Traslastion is ON': 'Auto-Translation is OFF' },
    { id: 'switch2', key: 'gen_ai_enabled', label: config.gen_ai_enabled?'GenAI Capability is ON': 'GenAI Capability is OFF' },
    { id: 'switch3', key: 'enforces_t_n_c', label: config.enforces_t_n_c?'Terms&Conditions Prompt Mandatory': 'Terms&Conditions Prompt Off' },
    { id: 'switch4', key: 'entry_status', label: config.entry_status ? 'Channel is Active' : 'Channel is OFF' },
    { id: 'switch5', key: 'human_agents_on', label:config.human_agents_on? 'Live Agents Escalation ON': 'Live Agents Escalation OFF' },
    { id: 'switch6', key: 'anti_spam_on', label:config.anti_spam_on? 'AntiSpam ON': 'AntiSpam OFF' },
    { id: 'switch7', key: 'extend_bot', label: 'Extend/relate to other bots' },
  ];

  return (
    <Card extra={'w-full h-auto p-3'}>
      {error && <div className="text-red-500 mb-4">{error}</div>}
      {success && <div className="text-green-500 mb-4">{success}</div>}
      <div className="relative mb-3 flex items-center justify-between pt-1">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Channel Config: {channel_name || 'Unknown'}
        </h4>
      </div>
      <div className="flex flex-col">
        {switchConfigs.map(({ id, key, label }) => (
          <div key={id} className="mt-4 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Switch
                id={id}
                checked={config[key]}
                onChange={handleSwitchChange(key)}
              />
              <label
                htmlFor={id}
                className="text-base font-medium text-navy-700 dark:text-white"
              >
                {label}
              </label>
            </div>
            {key === 'gen_ai_enabled' && config.gen_ai_enabled && (
              <div className="ml-8 mt-2">
                <label
                  htmlFor="min_ai_words"
                  className="text-base font-medium text-navy-700 dark:text-white"
                >
                  Minimum Words Length for AI
                </label>
                <input
                  type="number"
                  id="min_ai_words"
                  value={config.min_ai_words}
                  onChange={handleMinWordsChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  min="0"
                />
              </div>
            )}
          </div>
        ))}
        <button
          onClick={handleSubmit}
          className="mt-6 w-full rounded-md bg-indigo-600 py-2 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Save Changes
        </button>
      </div>
    </Card>
  );
};

export default ChannelConfig;